import { useToasts } from "@certn/cascadia";
import { Formik, FormikProps } from "formik";
import { useState, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { object, string } from "yup";

import { ActionListItemWithModal } from "./ActionListItemWithModal";
import { DeleteAccountReasons } from "./DeleteAccountReasons";
import { FormModal } from "./FormModal";
import { useFormSubmit } from "../../../hooks/api/useSubmit";

export function DeleteAccount() {
  const { t } = useTranslation();
  const [isReadyToDelete, setIsReadyToDelete] = useState(false);
  const formRef = useRef<FormikProps<any>>(null);
  const { show } = useToasts();
  const navigate = useNavigate();

  const submitForm = useFormSubmit("/internal/people/me/account/delete/", {
    onSuccess: (values) => {
      navigate("/auth/account-deleted");
    },
    raiseError: true,
  });

  const schema = object({
    reason: string().required(t<string>("Please select a reason for leaving.")),
  });

  const handleContinue = () => {
    setIsReadyToDelete(true);
  };

  return (
    <ActionListItemWithModal
      testID="help-and-privacy-delete-account-button"
      title={t("Delete your account")}
      description={t("Delete your Certn account and profile information")}
      link={t("Delete")}
    >
      {(close: () => void) => (
        <Formik
          initialValues={{ reason: "" }}
          onSubmit={async (values, helpers) => {
            try {
              await submitForm(values, helpers);
              close(); // once form submission successful, close modal
            } catch {
              show({
                title: t("Something went wrong, try again"),
                type: "error",
              });
            }
          }}
          innerRef={formRef}
          validationSchema={schema}
        >
          {({ handleSubmit }) => {
            return (
              <FormModal
                title={t("Delete your account")}
                onSubmit={isReadyToDelete ? handleSubmit : handleContinue}
                onCancel={() => {
                  setIsReadyToDelete(false);
                  close();
                }}
                continueText={isReadyToDelete ? t("Delete account") : undefined}
              >
                {isReadyToDelete ? (
                  <DeleteAccountReasons />
                ) : (
                  <div>
                    <Trans>
                      Deleting your account means you will no longer have access
                      to your Certn profile or account and all your data will be
                      deleted. You will instantly lose access to any background
                      check invites, and any historical background check
                      information. Pressing continue will also result in the
                      deletion of any data shared with 3rd parties. The data
                      deletion may take up to 30 days. Please contact
                      privacy@certn.co if you have any questions.
                    </Trans>
                    <br />
                    <br />
                    <Trans>Do you want to continue?</Trans>
                  </div>
                )}
              </FormModal>
            );
          }}
        </Formik>
      )}
    </ActionListItemWithModal>
  );
}
